// Dependency-injected so the logic can be unit-tested without dragging in the
// voice/livekit/auth stores (which transitively import @os/* siblings). useSocket
// wires the real store/socket calls; tests inject fakes.
export interface VoiceMovedDeps {
  getCurrentChannelId: () => string | null
  getUsername: () => string
  disconnect: () => Promise<void>
  setCurrentChannel: (toChannelId: string) => void
  emitJoin: (toChannelId: string) => void
  connect: (toChannelId: string, username: string) => Promise<unknown>
}

// Handle an admin "you've been moved to another voice channel" signal. The
// server has already rewritten our VoiceState row and broadcast the leave/join,
// so we only re-point LiveKit at the new room and re-register presence.
export async function handleVoiceMoved(
  data: { fromChannelId: string; toChannelId: string },
  deps: VoiceMovedDeps,
) {
  if (deps.getCurrentChannelId() !== data.fromChannelId) return
  const username = deps.getUsername()
  // OSPROD-221: AWAIT the old-room teardown before connecting to the new room.
  // The previous fire-and-forget order ran disconnect() and connect()
  // concurrently — and disconnect()'s releaseCallOwnership() raced against
  // connect()'s tryAcquireCallOwnership(), so the new room came up without
  // ownership and got torn down seconds later (user "heard the new room for a
  // few seconds, then snapped back to the old one"). Sequencing the teardown
  // first means connect() sees room=null, acquires ownership cleanly, and the
  // old SFU session is fully closed before the new one opens.
  await deps.disconnect()
  // Update store without emitting leave (server already handled it)
  deps.setCurrentChannel(data.toChannelId)
  // Re-register presence in the new room. The move rewrote our VoiceState row
  // but a voice:join is the ONLY thing that calls presence.markInVoiceRoom on
  // the server — without it we're absent from presence:voice:{toChannel}, so
  // the every-minute voice-ghost-killer treats the row as a phantom, deletes
  // it, and broadcasts left:true. Everyone's sidebar then drops us while the
  // LiveKit tile lingers (tile = remoteParticipants ground truth, independent
  // of presence). Mirrors the voice:join every other connect path emits
  // (VoiceManager onConnect / tryConnect / self-heal). No takeover flag — an
  // admin moved us; we didn't switch devices, so don't kick our other tabs.
  deps.emitJoin(data.toChannelId)
  // Join new LiveKit room (await so the teardown→reconnect stays sequential)
  await deps.connect(data.toChannelId, username).catch(() => {})
}
